import { Controller } from "@hotwired/stimulus"

// Sentence-builder edit modal for portfolio alert rules:
//   "Alert me when [metric] [op] [value] for [scope] via email to [addresses]
//    [frequency]"
// Live retroactive preview underneath — debounced POST to preview endpoint
// on every field change. No browser-side snapshot reads; always server-side.
//
// Opens via custom event "portfolio-alert-form:open" with { rule }. Refreshes
// the manage modal via "portfolio-alert-manage:refresh" after a successful save.

const METRIC_OPTIONS = [
  { value: "budget_variance_pct", label: "Budget variance (%)", unit: "%", numeric: true },
  { value: "budget_variance_amount", label: "Budget variance ($)", unit: "$", numeric: true, currency: true },
  { value: "schedule_days_behind", label: "Schedule delay (days)", unit: " days", numeric: true },
  { value: "overdue_items_count", label: "Overdue items", unit: "", numeric: true },
  { value: "rag_status_changed_to", label: "RAG status changed to", unit: "", numeric: false },
  { value: "project_count_red", label: "Projects in red (count)", unit: "", numeric: true, portfolioLevel: true }
]

const OP_OPTIONS = [
  { value: "gt", label: "is greater than" },
  { value: "gte", label: "is at least" },
  { value: "lt", label: "is less than" },
  { value: "lte", label: "is at most" },
  { value: "eq", label: "equals" }
]

const FREQUENCY_OPTIONS = [
  { value: "immediate", label: "immediately" },
  { value: "daily_digest", label: "as a daily digest" },
  { value: "weekly_digest", label: "as a weekly digest" }
]

const RAG_TARGETS = [
  { value: "red", label: "red" },
  { value: "amber", label: "amber" },
  { value: "green", label: "green" }
]

const SCOPE_OPTIONS = [
  { value: "all", label: "all projects" },
  { value: "portfolio", label: "a specific portfolio" },
  { value: "program", label: "a specific program" },
  { value: "phase", label: "a specific phase" }
]

export default class extends Controller {
  static targets = [
    "title", "errors", "bodyMount",
    "submitBtn", "testBtn"
  ]
  static values = {
    createUrl: String,
    updateUrl: String,
    previewUrl: String,
    portfolios: Array,
    programs: Array,
    phases: Array,
    userEmail: String
  }

  connect() {
    this._onOpen = this._onOpen.bind(this)
    document.addEventListener("portfolio-alert-form:open", this._onOpen)
    this._ruleId = null
    this._state = this._defaultState()
    this._previewTimer = null
  }

  disconnect() {
    document.removeEventListener("portfolio-alert-form:open", this._onOpen)
    if (this._previewTimer) clearTimeout(this._previewTimer)
  }

  _onOpen(event) {
    const rule = event.detail?.rule || null
    this._ruleId = rule?.id || null
    this._state = rule ? this._stateFromRule(rule) : this._defaultState()
    this.titleTarget.textContent = rule ? "Edit alert rule" : "New alert rule"
    this.testBtnTarget.classList.toggle("hidden", !rule)
    this._clearErrors()
    this._renderBody()
    this._runPreview()
  }

  _defaultState() {
    return {
      name: "",
      enabled: true,
      condition: {
        metric: "budget_variance_pct",
        op: "gt",
        value: 10,
        scope: { type: "all", value: null }
      },
      frequency: "immediate",
      recipients_text: this.userEmailValue || ""
    }
  }

  _stateFromRule(rule) {
    const condition = rule.condition || {}
    const scope = condition.scope || { type: "all", value: null }
    return {
      name: rule.name || "",
      enabled: rule.enabled !== false,
      condition: {
        metric: condition.metric || "budget_variance_pct",
        op: condition.op || "gt",
        value: condition.value,
        scope: { type: scope.type || "all", value: scope.value ?? null }
      },
      frequency: rule.frequency || "immediate",
      recipients_text: rule.recipients_text || (rule.email_recipients || []).join("\n")
    }
  }

  // ── Rendering ───────────────────────────────────────────────

  _renderBody() {
    const metric = this._state.condition.metric
    const metricDef = METRIC_OPTIONS.find(m => m.value === metric) || METRIC_OPTIONS[0]
    const scopeType = this._state.condition.scope.type || "all"

    const valueInputHTML = metric === "rag_status_changed_to"
      ? this._selectHTML("value", this._state.condition.value, RAG_TARGETS)
      : this._numericValueHTML(metricDef)

    // When metric is rag_status_changed_to, the operator is implicitly "eq" —
    // hide the op picker and force the op on change.
    const opHTML = metric === "rag_status_changed_to"
      ? `<span class="px-2 py-1 text-[13px] text-gray-600">changed to</span>`
      : this._selectHTML("op", this._state.condition.op, OP_OPTIONS)

    this.bodyMountTarget.innerHTML = `
      <div>
        <label class="block text-[13px] font-medium text-gray-700 mb-1">Rule name</label>
        <input type="text"
               data-role="name"
               value="${escapeAttr(this._state.name)}"
               placeholder="e.g. Schedule slipped over 30 days"
               class="w-full border border-gray-300 rounded px-2.5 py-1.5 text-[13px] focus:ring-1 focus:ring-brand focus:border-brand">
      </div>

      <div>
        <label class="block text-[13px] font-medium text-gray-700 mb-1">Rule</label>
        <div class="border border-gray-200 rounded-lg bg-gray-50 px-3 py-3 text-[13px] text-gray-700 flex items-center flex-wrap gap-2 leading-7">
          <span>Alert me when</span>
          ${this._selectHTML("metric", metric, METRIC_OPTIONS)}
          ${opHTML}
          ${valueInputHTML}
          <span>for</span>
          ${this._selectHTML("scope_type", scopeType, SCOPE_OPTIONS)}
          ${this._scopeValueHTML(scopeType)}
          <span>·</span>
          ${this._selectHTML("frequency", this._state.frequency, FREQUENCY_OPTIONS)}
        </div>
      </div>

      <div>
        <label class="block text-[13px] font-medium text-gray-700 mb-1">Email recipients</label>
        <textarea data-role="recipients"
                  rows="3"
                  placeholder="one email per line"
                  class="w-full border border-gray-300 rounded px-2.5 py-1.5 text-[13px] focus:ring-1 focus:ring-brand focus:border-brand font-mono">${escapeHTML(this._state.recipients_text)}</textarea>
        <p class="text-[11px] text-gray-500 mt-1">Use one address per line. Up to 20 recipients.</p>
      </div>

      <div data-role="preview"
           class="border border-gray-200 rounded-lg px-4 py-3 bg-blue-50/40">
        <div class="flex items-center gap-2 text-[13px] text-gray-700">
          <span class="inline-block w-2 h-2 rounded-full bg-gray-300" data-role="preview-dot"></span>
          <span data-role="preview-text">Evaluating…</span>
        </div>
      </div>
    `

    // Bind listeners
    this.bodyMountTarget.querySelector("[data-role='name']").addEventListener("input", e => {
      this._state.name = e.target.value
    })
    this.bodyMountTarget.querySelector("[data-role='recipients']").addEventListener("input", e => {
      this._state.recipients_text = e.target.value
    })
    this.bodyMountTarget.querySelectorAll("select[data-role]").forEach(select => {
      select.addEventListener("change", e => this._onSelectChange(e.target.dataset.role, e.target.value))
    })
    const valueInput = this.bodyMountTarget.querySelector("input[data-role='value']")
    if (valueInput) {
      valueInput.addEventListener("input", e => {
        this._state.condition.value = e.target.value
        this._schedulePreview()
      })
    }
    const scopeValueEl = this.bodyMountTarget.querySelector("select[data-role='scope_value']")
    if (scopeValueEl) {
      scopeValueEl.addEventListener("change", e => {
        this._state.condition.scope.value = e.target.value || null
        this._schedulePreview()
      })
    }
  }

  _onSelectChange(role, value) {
    const cond = this._state.condition
    if (role === "metric") {
      cond.metric = value
      // Reset value when switching between numeric and RAG
      if (value === "rag_status_changed_to") {
        cond.value = "red"
        cond.op = "eq"
      } else if (cond.op === "eq" && !["overdue_items_count", "project_count_red"].includes(value)) {
        cond.op = "gt"
      }
      if (!isFinite(parseFloat(cond.value)) && value !== "rag_status_changed_to") {
        cond.value = 10
      }
      this._renderBody()
    } else if (role === "op") {
      cond.op = value
    } else if (role === "scope_type") {
      cond.scope = { type: value, value: null }
      this._renderBody()
    } else if (role === "frequency") {
      this._state.frequency = value
    }
    this._schedulePreview()
  }

  _selectHTML(role, current, options) {
    const opts = options.map(opt =>
      `<option value="${escapeAttr(opt.value)}" ${String(opt.value) === String(current) ? "selected" : ""}>${escapeHTML(opt.label)}</option>`
    ).join("")
    return `<select data-role="${role}" class="border border-gray-300 rounded px-2 py-1 text-[13px] bg-white focus:ring-1 focus:ring-brand focus:border-brand">${opts}</select>`
  }

  _numericValueHTML(metricDef) {
    const isCurrency = metricDef.currency
    const prefix = isCurrency ? `<span class="text-gray-500">$</span>` : ""
    const suffix = metricDef.unit && !isCurrency ? `<span class="text-gray-500">${escapeHTML(metricDef.unit)}</span>` : ""
    return `
      <span class="inline-flex items-center gap-1 border border-gray-300 rounded px-2 py-1 bg-white">
        ${prefix}
        <input type="number"
               step="${metricDef.value === "schedule_days_behind" || metricDef.value === "overdue_items_count" || metricDef.value === "project_count_red" ? "1" : "0.1"}"
               data-role="value"
               value="${escapeAttr(this._state.condition.value ?? 0)}"
               class="w-20 text-[13px] focus:outline-none text-right">
        ${suffix}
      </span>
    `
  }

  _scopeValueHTML(scopeType) {
    if (scopeType === "all") return ""
    let options = []
    if (scopeType === "portfolio") options = (this.portfoliosValue || []).map(p => ({ value: p.id, label: p.name }))
    if (scopeType === "program") options = (this.programsValue || []).map(p => ({ value: p.id, label: p.name }))
    if (scopeType === "phase") options = (this.phasesValue || []).map(p => ({ value: p.value, label: p.label }))

    if (!options.length) {
      return `<span class="text-[11px] text-amber-700 bg-amber-50 rounded px-2 py-1">No options available</span>`
    }

    options.unshift({ value: "", label: "— choose —" })
    const current = this._state.condition.scope.value ?? ""
    return this._selectHTML("scope_value", current, options)
  }

  // ── Preview ─────────────────────────────────────────────────

  _schedulePreview() {
    if (this._previewTimer) clearTimeout(this._previewTimer)
    this._previewTimer = setTimeout(() => this._runPreview(), 400)
  }

  async _runPreview() {
    const condition = this._buildConditionPayload()
    this._setPreviewText("Evaluating…", "loading")
    try {
      const response = await fetch(this.previewUrlValue, {
        method: "POST",
        headers: jsonHeaders(),
        body: JSON.stringify({ condition, frequency: this._state.frequency, window_days: 30 })
      })
      const data = await response.json()
      if (!response.ok || !data.success) {
        this._setPreviewText((data.errors || ["Preview failed"]).join(" · "), "error")
        return
      }
      this._renderPreview(data.preview)
    } catch (err) {
      this._setPreviewText(err.message || "Network error", "error")
    }
  }

  _renderPreview(preview) {
    const fireCount = preview.fire_count
    const projects = preview.project_count
    const window = preview.window_days
    const snapshots = preview.snapshot_count

    if (preview.needs_more_history) {
      const need = snapshots === 0 ? "no snapshots yet" : `only ${snapshots} snapshot available`
      this._setPreviewText(`Would have fired 0 times — ${need}, need more history for a meaningful preview.`, "info")
      return
    }

    if (fireCount === 0) {
      this._setPreviewText(`Would have fired 0 times in the last ${window} days.`, "quiet")
    } else {
      const tail = projects > 0 ? ` across ${projects} project${projects === 1 ? "" : "s"}` : ""
      this._setPreviewText(`Would have fired ${fireCount} time${fireCount === 1 ? "" : "s"} in the last ${window} days${tail}.`, "active")
    }
  }

  _setPreviewText(text, state) {
    const textEl = this.bodyMountTarget.querySelector("[data-role='preview-text']")
    const dotEl = this.bodyMountTarget.querySelector("[data-role='preview-dot']")
    if (!textEl || !dotEl) return
    textEl.textContent = text
    dotEl.className = "inline-block w-2 h-2 rounded-full " + previewDotClass(state)
  }

  // ── Persistence ─────────────────────────────────────────────

  _buildConditionPayload() {
    const cond = this._state.condition
    const scopeValue = cond.scope.type === "all" ? null : cond.scope.value
    return {
      metric: cond.metric,
      op: cond.metric === "rag_status_changed_to" ? "eq" : cond.op,
      value: cond.value,
      scope: { type: cond.scope.type, value: scopeValue }
    }
  }

  async save() {
    this._clearErrors()
    this.submitBtnTarget.disabled = true
    const url = this._ruleId ? `${this.updateUrlValue}/${this._ruleId}` : this.createUrlValue
    const method = this._ruleId ? "PATCH" : "POST"
    const body = {
      name: this._state.name,
      enabled: this._state.enabled,
      frequency: this._state.frequency,
      condition: this._buildConditionPayload(),
      recipients_text: this._state.recipients_text,
      source: "sentence_builder"
    }
    try {
      const response = await fetch(url, { method, headers: jsonHeaders(), body: JSON.stringify(body) })
      const data = await response.json()
      if (!response.ok || !data.success) {
        this._showErrors(data.errors || ["Save failed"])
        return
      }
      this._toast(this._ruleId ? "Rule updated" : "Rule created", "notice")
      const modal = document.getElementById("portfolio-alert-form-modal")
      if (modal) { modal.classList.add("hidden"); document.body.style.overflow = "" }
      document.dispatchEvent(new CustomEvent("portfolio-alert-manage:refresh"))
    } catch (err) {
      this._showErrors([err.message || "Network error"])
    } finally {
      this.submitBtnTarget.disabled = false
    }
  }

  async sendTest() {
    if (!this._ruleId) return
    this.testBtnTarget.disabled = true
    try {
      const response = await fetch(`${this.updateUrlValue}/${this._ruleId}/send_test`, {
        method: "POST",
        headers: jsonHeaders()
      })
      const data = await response.json()
      if (!response.ok || !data.success) {
        this._showErrors(data.errors || ["Test send failed"])
        return
      }
      this._toast(`Test email queued to ${data.recipient}`, "notice")
    } catch (err) {
      this._showErrors([err.message || "Network error"])
    } finally {
      this.testBtnTarget.disabled = false
    }
  }

  _clearErrors() {
    this.errorsTarget.classList.add("hidden")
    this.errorsTarget.textContent = ""
  }

  _showErrors(messages) {
    this.errorsTarget.classList.remove("hidden")
    this.errorsTarget.textContent = messages.join(" · ")
  }

  _toast(message, type = "notice") {
    const toast = document.createElement("div")
    toast.className = type === "alert" ? "flash-toast-alert" : "flash-toast-notice"
    toast.setAttribute("data-controller", "flash")
    toast.setAttribute("data-flash-dismiss-after-value", "5000")
    toast.textContent = message
    document.body.prepend(toast)
  }
}

function jsonHeaders() {
  return {
    "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content,
    Accept: "application/json",
    "Content-Type": "application/json"
  }
}

function escapeHTML(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
}

function escapeAttr(value) {
  return escapeHTML(value).replace(/"/g, "&quot;")
}

function previewDotClass(state) {
  switch (state) {
    case "active": return "bg-emerald-500"
    case "quiet": return "bg-gray-400"
    case "info": return "bg-amber-400"
    case "error": return "bg-red-500"
    case "loading": return "bg-brand animate-pulse"
    default: return "bg-gray-300"
  }
}
